import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './HomePage.css';
import churchCrest from '../assets/Mar_Thoma_Syrian_Church_Crest.png';
import CommitteeMembers from './CommitteeMembers';
import MissionFieldReps from './MissionFieldReps';
import Bishops from './Bishops';
import OrgRepresentatives from './OrgRepresentatives';
import VicarMessages from './VicarMessages';
import ChurchTimes from './ChurchTimes';
import ChurchesAroundUs from './ChurchesAroundUs';
import PrayerMeetings from './PrayerMeetings';
import API_BASE_URL from '../config/api';

const tabs = [
  { id: 'directory', name: 'Parish Directory' },
  { id: 'committee', name: 'Committee Members' },
  { id: 'mission-field', name: 'Mission Field Representatives' },
  { id: 'bishops', name: 'Bishops' },
  { id: 'org-representatives', name: 'Organization Representatives' },
  { id: 'meetings', name: 'Prayer Meetings' },
  { id: 'message', name: "Vicar's Messages" },
  { id: 'church-times', name: 'Church Times' },
  { id: 'churches', name: 'Churches Around Us' },
  { id: 'misc', name: 'Miscellaneous' },
];

const HomePage = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ show: false, family: null, hofName: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPrayerGroup, setSelectedPrayerGroup] = useState('');
  const navigate = useNavigate();

  const handleLogout = () => {
    onLogout();
  };



  const handleEditClick = (family) => {
    navigate('/edit-family', { state: { family } });
  };

  const handleViewClick = (family) => {
    navigate('/view-family', { state: { family } });
  };

  const handleDeleteClick = (family) => {
    const hof = family.members?.find(member => 
      member.relationshipToHof === 'Head of Family'
    );
    const hofName = hof?.fullName || 'this family';
    setDeleteModal({ show: true, family, hofName });
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/families/${deleteModal.family.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        fetchFamilies();
      }
    } catch (error) {
      console.error('Delete failed:', error);
    }
    setDeleteModal({ show: false, family: null, hofName: '' });
  };

  const cancelDelete = () => {
    setDeleteModal({ show: false, family: null, hofName: '' });
  };

  const fetchFamilies = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/families`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFamilies(data);
      } else {
        setError('Failed to fetch families');
      }
    } catch (error) {
      setError('Error loading families');
    } finally {
      setLoading(false);
    }
  };

  // Get unique prayer groups for the filter dropdown
  const uniquePrayerGroups = [...new Set(families.map(family => family.prayerGroup).filter(Boolean))];

  // Filter families based on search term and prayer group
  const filteredFamilies = families.filter(family => {
    const hof = family.members?.find(member => member.relationshipToHof === 'Head of Family');
    const hofName = hof?.fullName || '';
    
    const matchesSearch = searchTerm === '' || 
      hofName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      family.id.toString().includes(searchTerm);
    
    const matchesPrayerGroup = selectedPrayerGroup === '' || 
      family.prayerGroup === selectedPrayerGroup;
    
    return matchesSearch && matchesPrayerGroup;
  });

  useEffect(() => {
    fetchFamilies();
  }, []);

  // Refresh families when returning to this page
  useEffect(() => {
    const handleFocus = () => {
      fetchFamilies();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  return (
    <div className="home-container">
      <header className="home-header">
        <div className="header-content">
          <div className="logo">
            <img src={churchCrest} alt="Church Crest" className="church-crest-icon-header" />
            <h1>STMTC Admin Portal</h1>
          </div>
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>
      
      <main className="home-main">
        <div className="tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.name}
            </button>
          ))}
        </div>

        <div className="tab-content">
          {activeTab === 'directory' && (
            <>
              <div className="directory-header">
                <button
                  className="add-family-btn"
                  onClick={() => navigate('/add-family')}
                >
                  Add Family
                </button>
              </div>

              <div className="search-filter-section">
                <div className="search-box">
                  <input
                    type="text"
                    placeholder="Search by HOF name or Family ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
                <div className="filter-box">
                  <select
                    value={selectedPrayerGroup}
                    onChange={(e) => setSelectedPrayerGroup(e.target.value)}
                    className="prayer-group-filter"
                  >
                    <option value="">All Prayer Groups</option>
                    {uniquePrayerGroups.map(group => (
                      <option key={group} value={group}>
                        {group}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="family-list-section">
                <h3>Family List</h3>
                {loading ? (
                  <div className="loading">Loading families...</div>
                ) : error ? (
                  <div className="error">{error}</div>
                ) : (
                  <div className="table-container">
                    <table className="family-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>HOF Photo</th>
                          <th>HOF Name</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFamilies.map(family => {
                          const hof = family.members?.find(member =>
                            member.relationshipToHof === 'Head of Family'
                          );
                          return (
                            <tr key={family.id}>
                              <td>{family.id}</td>
                              <td>
                                {family.hofPhotoUrl ? (
                                  <img
                                    src={family.hofPhotoUrl}
                                    alt="Head of Family"
                                    className="hof-photo"
                                  />
                                ) : (
                                  <span className="no-image">No Image</span>
                                )}
                              </td>
                              <td>{hof?.fullName || 'N/A'}</td>
                              <td>
                                <div className="action-buttons">
                                  <button className="icon-btn view-btn" onClick={() => handleViewClick(family)} title="View">
                                    👁️
                                  </button>
                                  <button className="icon-btn edit-btn" onClick={() => handleEditClick(family)} title="Edit">
                                    ✏️
                                  </button>
                                  <button className="icon-btn delete-btn" onClick={() => handleDeleteClick(family)} title="Delete">
                                    🗑️
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
          {activeTab === 'committee' && <CommitteeMembers />}
          {activeTab === 'mission-field' && <MissionFieldReps />}
          {activeTab === 'bishops' && <Bishops />}
          {activeTab === 'org-representatives' && <OrgRepresentatives />}
          {activeTab === 'meetings' && <PrayerMeetings />}
          {activeTab === 'message' && <VicarMessages />}
          {activeTab === 'church-times' && <ChurchTimes />}
          {activeTab === 'churches' && <ChurchesAroundUs />}
          {activeTab === 'misc' && <div><h2>Miscellaneous</h2><p>Content coming soon...</p></div>}
        </div>
      </main>
      
      {deleteModal.show && (
        <div className="modal-overlay">
          <div className="delete-modal">
            <div className="modal-icon">⚠️</div>
            <h3>Delete Family</h3>
            <p>Are you sure you want to delete <strong>{deleteModal.hofName}</strong>?</p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="modal-buttons">
              <button className="cancel-btn" onClick={cancelDelete}>Cancel</button>
              <button className="confirm-delete-btn" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
